const DAY_MS = 24 * 60 * 60 * 1000;
const SITE_ACTIVITY_PATTERN =
	/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

export type RovoTargetingAudienceStatus =
	| 'loading'
	| 'eligible'
	| 'ineligible'
	| 'unavailable'
	| 'outsideRollout';
export type RovoTargetingSignal = 'usage' | 'siteActivity' | 'both' | 'none';
export type RovoTargetingReason = 'missing' | 'invalid' | 'requestFailure' | 'timeout' | 'none';
export type RovoTargetingEvaluation = {
	outcome: Exclude<RovoTargetingAudienceStatus, 'loading'>;
	signal: RovoTargetingSignal;
	reason: RovoTargetingReason;
	durationMs?: number;
};

export const parseSiteActivityTimestamp = (value: unknown): number | undefined => {
	if (typeof value !== 'string' || !SITE_ACTIVITY_PATTERN.test(value)) return undefined;
	const [year, month, day] = value.slice(0, 10).split('-').map(Number);
	const date = new Date(Date.UTC(year, month - 1, day));
	if (
		date.getUTCFullYear() !== year ||
		date.getUTCMonth() !== month - 1 ||
		date.getUTCDate() !== day
	)
		return undefined;
	const timestamp = Date.parse(value);
	return Number.isFinite(timestamp) ? timestamp : undefined;
};

export const evaluateRovoTargetingAudience = (
	activeDays: unknown,
	lastActive: unknown,
	minimumActiveDays: number,
	maximumActiveDays: number,
	now: number = Date.now(),
): RovoTargetingEvaluation => {
	if (
		!Number.isInteger(minimumActiveDays) ||
		!Number.isInteger(maximumActiveDays) ||
		minimumActiveDays < 0 ||
		minimumActiveDays > 28 ||
		maximumActiveDays < 0 ||
		maximumActiveDays > 28 ||
		minimumActiveDays > maximumActiveDays
	) {
		return { outcome: 'unavailable', signal: 'none', reason: 'invalid' };
	}
	const missingUsage = activeDays === undefined;
	const missingActivity = lastActive === undefined;
	const timestamp = parseSiteActivityTimestamp(lastActive);
	const invalidUsage =
		typeof activeDays !== 'number' ||
		!Number.isInteger(activeDays) ||
		activeDays < 0 ||
		activeDays > 28;
	const invalidActivity = timestamp === undefined || timestamp > now;
	if (missingUsage || missingActivity || invalidUsage || invalidActivity) {
		const badUsage = missingUsage || invalidUsage;
		const badActivity = missingActivity || invalidActivity;
		const presentInvalidSignal =
			(!missingUsage && invalidUsage) || (!missingActivity && invalidActivity);
		return {
			outcome: 'unavailable',
			signal: badUsage && badActivity ? 'both' : badUsage ? 'usage' : 'siteActivity',
			reason: presentInvalidSignal ? 'invalid' : 'missing',
		};
	}
	const nowDate = new Date(now);
	const todayUtc = Date.UTC(nowDate.getUTCFullYear(), nowDate.getUTCMonth(), nowDate.getUTCDate());
	const recent = timestamp! >= todayUtc - 27 * DAY_MS;
	const eligible = activeDays >= minimumActiveDays && activeDays <= maximumActiveDays && recent;
	return { outcome: eligible ? 'eligible' : 'ineligible', signal: 'none', reason: 'none' };
};
